import React, { useCallback, useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import http from "services/http";
import PermissionGate from "utils/PermissionGate";
import dayjs from 'dayjs';
import { alerta } from "components/Alert/index";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import formatMoney from "utils/formatMoney";
import {moedaInput, parseMoeda} from 'utils/moedaInput';
import Pagination from "components/Paginacao";
import "components/Paginacao/style.css";

// Dígitos e números por bilhete ficam travados em 4/4 no backend enquanto o app
// antigo (2 Pra 500) estiver em campo — ele só sabe imprimir milhar de 4 dígitos
// em grupos de 4. Para reabrir, devolver os selects e voltar a enviar os campos.
const CREDENCIAIS_PADRAO = {};

// Mesmo valor do backend (paginate(30) em ListaBilhetinhoService::listarWeb).
const POR_PAGINA = 30;

const CHECKS_PADRAO = [
  // Bilhete avulso: sai pelo valor cheio, sem desconto. Vem marcado por padrão
  // porque sem ele o menor pacote marcado vira o piso da venda no app.
  { checked: true, valor: '', quantidade: 1 },
  { checked: false, valor: '', quantidade: 3 },
  { checked: false, valor: '', quantidade: 6 },
  { checked: false, valor: '', quantidade: 13 },
  { checked: false, valor: '', quantidade: 27 },
];

const SITUACOES = {
  cancelada: { label: 'Cancelada', cor: 'warning' },
  encerrada: { label: 'Encerrada', cor: 'danger' },
  em_andamento: { label: 'Em andamento', cor: 'success' },
};

const pacotes = [
  { nome: 'um', quantidade: 1 },
  { nome: 'tres', quantidade: 3 },
  { nome: 'seis', quantidade: 6 },
  { nome: 'treze', quantidade: 13 },
  { nome: 'vinteSete', quantidade: 27 },
];

const indexPorNome = Object.fromEntries(pacotes.map((pacote, idx) => [pacote.nome, idx]));

// Declarado fora do componente de propósito: dentro do pai ele virava um tipo
// novo a cada render, então o React remontava o campo e a edição aberta se perdia
// a cada atualização da lista.
function CampoEncerramento({ el, aoAlterar }) {
  const [editarData, setEditarData] = useState(false);
  const [data, setData] = useState(dayjs(el.data_limite).format('YYYY-MM-DDTHH:mm'));

  const salvar = async () => {
    try {
      const response = await http.patch('/bilhetinho/' + el.id, { 'data_limite': data })
      setEditarData(false)
      alerta(response.data)
      // A lista é ordenada por encerramento: mudou a data, a linha muda de lugar.
      aoAlterar()
    } catch (error) {
      alerta(error.response.data)
    }
  }

  if (!editarData) {
    return (
      <>
        <span style={{ whiteSpace: 'nowrap' }}>{dayjs(el.data_limite).format('DD/MM/YYYY HH:mm')}</span>
        <button
          type="button"
          className="btn btn-secondary ml-1 p-0 text-center"
          style={{ height: '25px', width: '25px' }}
          title="Alterar encerramento"
          onClick={() => setEditarData(true)}
        >
          <i className="fa fa-pencil" aria-hidden="true" />
        </button>
      </>
    );
  }

  return (
    <div className="d-flex">
      <input
        required
        type="datetime-local"
        className="form-control"
        value={data}
        onChange={(e) => setData(e.target.value)}
        name='data_limite_nova'
      />
      <button
        type="button"
        className="btn btn-secondary ml-1 p-0 text-center"
        style={{ height: '38px', width: '25px' }}
        title="Salvar"
        onClick={salvar}
      >
        <i className="fa fa-check" aria-hidden="true"></i>
      </button>
      <button
        type="button"
        className="btn btn-secondary ml-1 p-0 text-center"
        style={{ height: '38px', width: '25px' }}
        title="Cancelar edição"
        onClick={() => {
          setEditarData(false)
          setData(dayjs(el.data_limite).format('YYYY-MM-DDTHH:mm'))
        }}
      >
        <i className="fa fa-close" aria-hidden="true"></i>
      </button>
    </div>
  );
}

export default function Bilhetinho() {
  const [loading, setLoading] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [credenciais, setCredenciais] = useState({ ...CREDENCIAIS_PADRAO });
  const [checks, setChecks] = useState(CHECKS_PADRAO);
  const [milharEsconde, setMilharEsconde] = useState(false);
  const [lista, setLista] = useState([]);
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(1);
  const [filtros, setFiltros] = useState({ status: 'todos', data_inicio: '', data_fim: '' });

  const defineCredenciais = (ev) => {
    const { name, value } = ev.target;

    setCredenciais({
      ...credenciais
      , [name]: value
    })
  }

  const premioValor = (v) => (v ? parseMoeda(v) : 0);

  const buscarBilhetinho = useCallback(async () => {
    try {
      setCarregando(true);
      const { data: { data: paginador } } = await http.get('/bilhetinho/listagem', {
        params: {
          page: pagina,
          per_page: POR_PAGINA,
          status: filtros.status,
          data_inicio: filtros.data_inicio || undefined,
          data_fim: filtros.data_fim || undefined,
        }
      });

      setLista(paginador.data ?? []);
      setTotal(paginador.total ?? 0);

      // Cancelar o último sorteio da página deixaria o operador olhando para uma
      // página vazia sem entender por quê.
      if ((paginador.data ?? []).length === 0 && pagina > 1) {
        setPagina((p) => p - 1);
      }
    } catch (e) {
      alerta(e.response?.data ?? { success: false, message: 'Erro ao listar 2 Pra 500!' })
    } finally {
      setCarregando(false);
    }
  }, [pagina, filtros]);

  const mudaFiltro = (campo, valor) => {
    setPagina(1);
    setFiltros((atual) => ({ ...atual, [campo]: valor }));
  };

  const mudarPagina = (page) => {
    setPagina(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const salvarBilhetinho = async (e) => {

    e.preventDefault();

    let milhares = {
      'milhar_premiada': '',
      'milhar_esconder': ''
    };

    if (milharEsconde) {
      const { value: formValues } = await Swal.fire({
        html:
          '<label>Milhar premiada</label><input id="swal-input1" style="width: 30px; margin: 3px"><br/>' +
          '<label>Milhar escondida</label><input id="swal-input2" style="width: 30px; margin: 3px">',
        focusConfirm: false,
        preConfirm: () => {
          return [
            document.getElementById('swal-input1').value,
            document.getElementById('swal-input2').value
          ]
        }
      })

      if (formValues) {
        milhares = {
          'milhar_premiada': formValues[0],
          'milhar_esconder': formValues[1]
        };
      }
    }

    await toast.promise(
      http.post('/bilhetinho', {
        ...credenciais,
        ...milhares,
        tipo_resultado: 'normal',
        valor: parseMoeda(credenciais.valor),
        numeros: checks.map((c) => ({...c, valor: parseMoeda(c.valor)})),
        premios: [
          premioValor(credenciais.primeiro_premio),
          premioValor(credenciais.segundo_premio),
          premioValor(credenciais.terceiro_premio),
          premioValor(credenciais.quarto_premio),
          premioValor(credenciais.quinto_premio)
        ]
      }),
      {
        pending: {
          render() {
            setLoading(true)
            return 'Gerando palpites, aguarde!'
          }
        },
        success: {
          render({ data }) {
            setLoading(false)
            setCredenciais({ ...CREDENCIAIS_PADRAO });
            setChecks(CHECKS_PADRAO);
            setPagina(1);
            buscarBilhetinho();
            document.forms[0].reset();
            return data.data.message
          }
        },
        error: {
          render({ data }) {
            setLoading(false)
            alerta(data.response.data);
          }
        }
      }
    );

  }

  const cancelaBilhetinho = async (id) => {

    Swal.fire({
      title: 'Deseja cancelar o 2 Pra 500?',
      text: 'Só será possivel recuperar gerando novos palpites, cuidado ao cancelar!',
      type: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      reverseButtons: true,
      confirmButtonText: 'Sim, cancelar!',
      cancelButtonText: 'Não',
    }).then(async (result) => {
      if (result.value) {
        try {
          const response = await http.delete('/bilhetinho/' + id)
          buscarBilhetinho()
          alerta(response.data)
        } catch (error) {
          alerta(error.response.data)
        }
      }
    });

  }

  const alternarAtivo = async (id, ativo) => {
    try {
      const response = await http.patch('/bilhetinho/' + id, { ativo })
      buscarBilhetinho()
      alerta(response.data)
    } catch (error) {
      alerta(error.response.data)
    }
  }

  const editarBilhetinho = async (el) => {
    const numero = (v) => (v == null ? '' : v);

    const { value: valores } = await Swal.fire({
      title: 'Editar 2 Pra 500',
      html:
        `<div style="text-align:left">
          <label>Valor</label>
          <input id="edit-valor" type="number" step="0.01" class="swal2-input" value="${numero(el.valor)}">
          <label>1º Prêmio</label>
          <input id="edit-p1" type="number" step="0.01" class="swal2-input" value="${numero(el.primeiro_premio)}">
          <label>2º Prêmio</label>
          <input id="edit-p2" type="number" step="0.01" class="swal2-input" value="${numero(el.segundo_premio)}">
          <label>3º Prêmio</label>
          <input id="edit-p3" type="number" step="0.01" class="swal2-input" value="${numero(el.terceiro_premio)}">
          <label>4º Prêmio</label>
          <input id="edit-p4" type="number" step="0.01" class="swal2-input" value="${numero(el.quarto_premio)}">
          <label>5º Prêmio</label>
          <input id="edit-p5" type="number" step="0.01" class="swal2-input" value="${numero(el.quinto_premio)}">
          <label><input id="edit-ativo" type="checkbox" ${el.ativo ? 'checked' : ''}> Ativo</label>
        </div>`,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Salvar',
      cancelButtonText: 'Cancelar',
      preConfirm: () => {
        const num = (id) => {
          const v = document.getElementById(id).value;
          return v === '' ? 0 : Number(v);
        };
        return {
          valor: num('edit-valor'),
          ativo: document.getElementById('edit-ativo').checked,
          premios: [num('edit-p1'), num('edit-p2'), num('edit-p3'), num('edit-p4'), num('edit-p5')],
        };
      }
    });

    if (!valores) return;

    try {
      const response = await http.patch('/bilhetinho/' + el.id, valores)
      buscarBilhetinho()
      alerta(response.data)
    } catch (error) {
      alerta(error.response.data)
    }
  }

  const checkedNumeros = (e) => {
    const { name, checked, value, id } = e.target;
    const isCheck = id === 'check';
    const idx = indexPorNome[name];
    if (idx === undefined) return;

    const novo = [...checks];

    if (isCheck) {
      novo[idx] = { ...novo[idx], checked };
    } else {
      novo[idx] = { ...novo[idx], valor: moedaInput(value) };
    }

    setChecks(novo);
  };

  // O campo do pacote guarda o DESCONTO, não o preço final. Mostrar quanto o
  // cliente paga tira a dúvida no ato de preencher, que é onde ela acontece.
  const precoPacote = (idx) => {
    const bruto = parseMoeda(credenciais.valor) * pacotes[idx].quantidade;
    if (!bruto) return null;

    // max(0, ...) espelha o piso que o backend aplica em CriaApostasService.
    return Math.max(0, bruto - parseMoeda(checks[idx].valor));
  };

  useEffect(() => {

    buscarBilhetinho()

  }, [buscarBilhetinho])

  useEffect(() => {

    http.get('/bancas/opcoes')
      .then(({ data: { data: opcoes } }) => setMilharEsconde(opcoes.opcoes.dois_pra_quinhentos))
      .catch((e) => alerta(e.response?.data ?? { success: false, message: 'Erro ao carregar opções!' }))

  }, [])

  return (
    <>
      <div className="titulo-principal">2 Pra 500</div>
      <form onSubmit={salvarBilhetinho}>
        <div className="conteudo-principal p-2">

          {/* Linha 1 — campos principais */}
          <div className="row mx-0 mb-2">
            <div className="col-3 px-1">
              <label>Encerramento</label>
              <input
                required
                type="datetime-local"
                className="form-control"
                onChange={defineCredenciais}
                name='data_limite'
              />
            </div>

            <div className="col-2 px-1">
              <label>Valor</label>
              <input
                required
                type="text"
                inputMode="numeric"
                className="form-control"
                placeholder="0,00"
                value={credenciais.valor ?? ''}
                onChange={(e) => setCredenciais({...credenciais, valor: moedaInput(e.target.value)})}
              />
            </div>

          </div>

          {/* Linha 2 — pacotes promocionais */}
          <div className="row mx-0 mb-2">
            {pacotes.map((pacote, idx) => {
              // O bilhete avulso não tem desconto: o valor cheio já é o menor
              // preço possível, e o backend zera qualquer desconto enviado aqui.
              const avulso = pacote.quantidade === 1;
              const preco = precoPacote(idx);

              return (
                <div className="col px-1" key={pacote.nome}>
                  {/* Sem a classe `d-flex` de propósito: o global.css a redefine
                      com space-between, o que jogava o checkbox para o canto
                      oposto do rótulo. */}
                  <label className="mb-1">
                    <input
                      type='checkbox'
                      id='check'
                      className="mr-1"
                      name={pacote.nome}
                      checked={checks[idx].checked}
                      onChange={checkedNumeros}
                    />
                    {pacote.quantidade} bilhete{avulso ? '' : 's'}
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    className="form-control"
                    onChange={checkedNumeros}
                    placeholder={avulso ? 'Valor cheio' : 'Desconto'}
                    value={checks[idx].valor}
                    name={pacote.nome}
                    id='valorCheck'
                    disabled={avulso || !checks[idx].checked}
                  />
                  {checks[idx].checked && preco !== null && (
                    <small className="text-muted d-block" style={{ whiteSpace: 'nowrap' }}>
                      cliente paga <b>{formatMoney(preco)}</b>
                    </small>
                  )}
                </div>
              );
            })}
          </div>

          {/* O aviso fica recolhido: a frase que importa sempre visível, os
              exemplos a um clique, sem ocupar um terço do formulário. */}
          <details className="alert alert-danger py-1 px-2 mb-2" style={{ fontSize: '0.85em' }}>
            <summary style={{ cursor: 'pointer', fontWeight: 700 }}>
              O valor do pacote é o DESCONTO, não o preço final — ver exemplos
            </summary>
            <div className="mt-1">
              Não é o valor por bilhete nem quanto o cliente vai pagar: é quanto se abate do total.
              <ul className="mb-1 mt-1 pl-4">
                <li>6 bilhetes de R$ 2,00 custam R$ 12,00. Preenchendo <b>R$ 2,00</b>, o cliente paga <b>R$ 10,00</b>.</li>
                <li>3 bilhetes de R$ 2,00 custam R$ 6,00. Preenchendo <b>R$ 1,00</b>, o cliente paga <b>R$ 5,00</b>.</li>
              </ul>
              O desconto vale só na quantidade exata do pacote: quem compra 7 bilhetes, com pacotes de 6 e 13
              cadastrados, não recebe desconto nenhum.
            </div>
          </details>

          {/* Linha 3 — prêmios + salvar */}
          <div className="row mx-0">
            {[
              { label: '1º Prêmio', key: 'primeiro_premio', required: true },
              { label: '2º Prêmio', key: 'segundo_premio', required: false },
              { label: '3º Prêmio', key: 'terceiro_premio', required: false },
              { label: '4º Prêmio', key: 'quarto_premio', required: false },
              { label: '5º Prêmio', key: 'quinto_premio', required: false },
            ].map(({ label, key, required }) => (
              <div className="col-2 px-1" key={key}>
                <label>{label}</label>
                <input
                  required={required}
                  type="text"
                  inputMode="numeric"
                  className="form-control"
                  placeholder="0,00"
                  value={credenciais[key] ?? ''}
                  onChange={(e) => setCredenciais({...credenciais, [key]: moedaInput(e.target.value)})}
                />
              </div>
            ))}

            <div className="col-2 px-1 d-flex align-items-end">
              <PermissionGate permissions={['Criar bilhetinho']}>
                <button
                  type="submit"
                  className="btn btn-info w-100"
                  style={{ height: '38px' }}
                  disabled={loading}
                >
                  Salvar
                </button>
              </PermissionGate>
            </div>
          </div>

        </div>
      </form>

      {/* Filtros da listagem — vão para o backend junto com a paginação */}
      <div className="conteudo-principal mt-2">
        <div className="row mx-0">
          <div className="col-4 px-1">
            <label>Situação</label>
            <select
              className="form-control"
              value={filtros.status}
              onChange={(e) => mudaFiltro('status', e.target.value)}
            >
              <option value="todos">Todas</option>
              <option value="em_andamento">Em andamento</option>
              <option value="encerrados">Encerradas</option>
              <option value="cancelados">Canceladas</option>
            </select>
          </div>
          <div className="col-4 px-1">
            <label>Encerramento de</label>
            <input
              type="date"
              className="form-control"
              value={filtros.data_inicio}
              onChange={(e) => mudaFiltro('data_inicio', e.target.value)}
            />
          </div>
          <div className="col-4 px-1">
            <label>Encerramento até</label>
            <input
              type="date"
              className="form-control"
              value={filtros.data_fim}
              onChange={(e) => mudaFiltro('data_fim', e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="conteudo-principal p-0 mt-2">
        <div className="table-responsive">
          <table className="table text-left border text-center mb-0">
            <thead>
              <tr>
                <th>Criação</th>
                <th>Encerramento</th>
                <th>Valor</th>
                <th>Prêmio</th>
                <th>Venda</th>
                <th>Bilhetes</th>
                <th>Ver</th>
                <th>Status</th>
                <th>Editar</th>
                <th>Cancelar</th>
              </tr>
            </thead>
            <tbody>
              {carregando ? (
                <tr>
                  <td colSpan="10" className="text-muted p-4">Carregando...</td>
                </tr>
              ) : lista.length === 0 ? (
                <tr>
                  <td colSpan="10" className="text-muted p-4">Nenhum sorteio encontrado.</td>
                </tr>
              ) : lista.map(el => {
                const situacao = SITUACOES[el.status] ?? SITUACOES.em_andamento;
                const vendidoPct = el.total_bilhetes
                  ? (el.vendidos * 100) / el.total_bilhetes
                  : 0;

                return (
                <tr key={el.id}>
                  <td style={{ whiteSpace: 'nowrap' }}>{dayjs(el.created_at).format('DD/MM/YYYY HH:mm')}</td>
                  <td style={{ maxWidth: "180px" }}>
                    <CampoEncerramento el={el} aoAlterar={buscarBilhetinho} />
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>{formatMoney(el.valor)}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{formatMoney(el.primeiro_premio ?? 0)}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{formatMoney(el.venda)}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {el.vendidos} / {el.total_bilhetes}
                    <div className="progress mx-auto mt-1" style={{ height: '4px', width: '60px' }}>
                      <div className="progress-bar bg-info" style={{ width: `${vendidoPct}%` }} />
                    </div>
                  </td>
                  <td>
                    <NavLink
                      exact
                      className="btn btn-info"
                      title="Ver bilhetes"
                      to={{ pathname: `/bilhetinho/${el.id}/bilhetes` }}
                    >
                      <i className="fa fa-eye" aria-hidden="true" />
                    </NavLink>
                  </td>
                  <td>
                    <span className={`badge badge-${situacao.cor}`}>{situacao.label}</span>
                    {!el.cancelado_em && (
                      <button
                        type="button"
                        className={`btn btn-sm btn-${el.ativo ? 'outline-secondary' : 'outline-success'} d-block mx-auto mt-1 px-1`}
                        onClick={() => alternarAtivo(el.id, !el.ativo)}
                      >
                        {el.ativo ? 'Desativar' : 'Ativar'}
                      </button>
                    )}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      title="Editar sorteio"
                      disabled={!!el.cancelado_em}
                      onClick={() => editarBilhetinho(el)}
                    >
                      <i className="fa fa-pencil" aria-hidden="true" />
                    </button>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-link text-danger"
                      title="Cancelar sorteio"
                      disabled={!!el.cancelado_em}
                      onClick={() => cancelaBilhetinho(el.id)}
                    >
                      <i className="fa fa-trash" aria-hidden="true" />
                    </button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {!carregando && total > 0 && (
          <div className="d-flex justify-content-between align-items-center px-2 py-2">
            <small className="text-muted">
              {(pagina - 1) * POR_PAGINA + 1}–{Math.min(pagina * POR_PAGINA, total)} de {total} sorteios
            </small>
            <Pagination
              currentPage={pagina}
              totalCount={total}
              pageSize={POR_PAGINA}
              onPageChange={mudarPagina}
            />
          </div>
        )}
      </div>
    </>
  )
}
